/* eslint-disable max-lines -- Pre-existing violation */
/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {
  Card,
  DataStatus,
  Dialog,
  Form,
  FormButton,
  FormField,
  type FormControl,
  type FormSubmitHandler,
  Separator,
  Table,
  TableRow,
  closeCurrentDialog,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useRef, useState, type MutableRefObject} from 'react';
import {useController, useWatch, type UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  newVehicleApi,
  useArchiveColourMutation,
  useArchiveUpholsteryMutation,
  useCreateColourMutation,
  useCreateUpholsteryMutation,
  useGetColourUpholsteryRestrictionsQuery,
  useListColourQuery,
  useListTrimQuery,
  useListUpholsteryQuery,
  usePutColourTrimsMutation,
  usePutColourUpholsteryRestrictionsMutation,
  usePutTrimUpholsteryMutation,
  useUpdateColourMutation,
  useUpdateUpholsteryMutation,
  type ColourResponseBody,
  type ColourWriteRequestBody,
  type OfferedTrimResponseBody,
  type TrimResponseBody,
  type UpholsteryResponseBody,
  type UpholsteryWriteRequestBody,
} from '@dms/api/new-vehicle';
import {MoneyText, useNewVehicleCatalogueLabels} from '@dms/features/new-vehicle';
import {useVehicleCatalogue} from '@dms/shared';

import type {ApiError, Nullish} from 'shared/web';

import {
  actionsColumn,
  archiveRowAction,
  Cell,
  ColourSwatch,
  column,
  editRowAction,
  FormSection,
  handleCatalogueError,
  isStaleCatalogueVersion,
  joinParts,
  OfferedOn,
  hasAnySwatch,
  PASSENGER_CAR,
  RowActions,
  SwatchLabel,
  TabIntro,
  TOGGLE_COLUMN_WIDTH,
  ToggleTile,
  t,
} from './catalogueUi';
import {colourSchema} from './colourSchema';
import {OfferedTrimsTable} from './components/OfferedTrimsTable';
import {RetryReadButton, type ItemReadAgain} from './components/RetryReadButton';
import {TrimPrices} from './components/TrimPrices';
import {GrossPriceField} from './GrossPriceField';
import {useGroupedNewTrims} from './hooks/useGroupedNewTrims';
import {useOpenPricedItemGroup} from './hooks/useOpenPricedItemGroup';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {PriceRow} from './PriceRow';
import {PriceWithGross} from './PriceWithGross';
import {ITEM_CHANGE_CANCELLED} from './types/PricedItemChangeOutcome';
import {upholsterySchema} from './upholsterySchema';
import {useCatalogueVatRate} from './useCatalogueVatRate';
import {useMatrixSaveQueue} from './useMatrixSaveQueue';
import {usePublishedPriceSave} from './usePublishedPriceSave';
import {addToGroup} from './utils/addEntriesToGroup';
import {colourArchiveChange} from './utils/colourArchiveChange';
import {colourCreateEntry} from './utils/colourCreateEntry';
import {colourTrimsEntry} from './utils/colourTrimsEntry';
import {changesKeptPrice, memberRows, offeredTrimChanges} from './utils/offeredTrimChanges';
import {
  changedTrimPrices,
  publishedOnePriceAtATime,
  toOfferedTrimRows,
  toOfferedTrims,
  withStoredPrices,
} from './utils/offeredTrimRows';
import {rebaseForm} from './utils/rebaseForm';
import {upholsteryArchiveChange} from './utils/upholsteryArchiveChange';
import {upholsteryCreateChange} from './utils/upholsteryCreateChange';

const replaceFactoryCode = (
  codes: string[],
  previous: string | null | undefined,
  next: string
): string[] => {
  if ((previous ?? '') === next) {
    return codes;
  }
  if (previous && codes.includes(previous)) {
    return next
      ? codes.map((code) => (code === previous ? next : code))
      : codes.filter((code) => code !== previous);
  }
  return next ? [...codes, next] : codes;
};
type ColourValues = z.infer<typeof colourSchema>;

const newColourBody = (modelYearId: string, values: ColourValues): ColourWriteRequestBody => ({
  modelYearId,
  name: values.name,
  codes: values.factoryCode ? [values.factoryCode] : [],
  kind: values.kind || null,
  hex: values.hex || null,
  factoryCode: values.factoryCode || null,
  version: 1,
});
type ColourScalarValues = Pick<ColourValues, 'name' | 'kind' | 'factoryCode' | 'hex'>;
type PersistedColour = {
  id: string;
  version: number;
  codes: string[];
  values: ColourScalarValues;
};
type UpholsteryValues = z.infer<typeof upholsterySchema>;
const EMPTY_UPHOLSTERY: UpholsteryValues = {
  name: '',
  fabric: '',
  factoryCode: '',
  hex: '',
  priceNet: '0.00',
  costNet: '',
};

/**
 * After a version conflict, adopt the colour's current version (and codes) so a retry does not
 * resend the stale one. The saved scalar values stay, so a retry still skips an update it already
 * made and only repeats the trim assignment against the fresh version.
 */
function useRefreshPersistedColourVersion() {
  const [getColour] = newVehicleApi.useLazyGetColourQuery();
  return async (persisted: MutableRefObject<PersistedColour | null>) => {
    const known = persisted.current;
    if (!known) {
      return;
    }
    const current = await getColour({colourId: known.id}, false)
      .unwrap()
      .catch(() => null);
    if (current && persisted.current?.id === known.id) {
      persisted.current = {...known, version: current.version, codes: current.codes};
    }
  };
}

const getColourScalarValues = (values: ColourValues): ColourScalarValues => ({
  name: values.name,
  kind: values.kind,
  factoryCode: values.factoryCode,
  hex: values.hex,
});

type ColourFieldsProps = {
  control: FormControl<ColourValues>;
  modelYearId: string;
  /** The trims the colour can be offered on: stored ones, and on a create the grouped new ones. */
  trims: Pick<TrimResponseBody, 'id' | 'name' | 'publicationStatus'>[];
  stored: OfferedTrimResponseBody[];
  currency: string;
  canViewCost: boolean;
  isCreate?: boolean;
  submitTitle: string;
  onSamePrice?: (priceNet: string, onCreated: () => void) => Promise<boolean>;
  colourRead?: ItemReadAgain;
};

function ColourFields(props: ColourFieldsProps) {
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const vatRate = useCatalogueVatRate(props.modelYearId);
  const [name, priceNet, costNet] = useWatch({
    control: props.control,
    name: ['name', 'priceNet', 'costNet'],
  });
  const trims = useController({control: props.control, name: 'trims'});
  const follow = (key: 'priceNet' | 'costNet', previous: string | null) => (next: string | null) =>
    trims.field.onChange(
      trims.field.value.map((row) =>
        row.isOffered && row[key] === (previous ?? '') ? {...row, [key]: next ?? ''} : row
      )
    );
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.section.identity', 'Identity')}>
        <FormField
          control={props.control}
          name="name"
          type="text"
          label={t('general.labels.name', 'Name')}
          placeholder="Frozen White"
          isRequired={props.isCreate}
        />
        <Grid columns={3} spacing={3}>
          <FormField
            control={props.control}
            name="kind"
            type="choice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.kind', 'Kind')}
            options={catalogue.bodyColorTypeOptions ?? []}
            isLoading={isLoading}
            isDisabled={isNilOrEmpty(catalogue.bodyColorTypeOptions)}
          />
          <FormField
            control={props.control}
            name="factoryCode"
            type="text"
            label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          />
          <FormField
            control={props.control}
            name="hex"
            type="text"
            label={t('entity.newVehicle.catalogue.hex', 'Hex')}
            placeholder="#1A2B3C"
            helperText={t(
              'entity.newVehicle.catalogue.hexHelp',
              'Used for the swatch salespeople see.'
            )}
          />
        </Grid>
      </FormSection>
      <Show when={props.isCreate}>
        <Separator spacing={0} />
        <FormSection title={t('entity.newVehicle.catalogue.section.price', 'Price')}>
          <PriceRow vatRate={vatRate} canViewCost={props.canViewCost} spacing={3}>
            <GrossPriceField
              control={props.control}
              name="priceNet"
              vatRate={vatRate}
              label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
              helperText={t(
                'entity.newVehicle.catalogue.priceFillsTrimsHelp',
                'Fills the price on each trim you tick below; change a single trim there. 0 means included.'
              )}
              onChange={follow('priceNet', priceNet)}
            />
            <Show when={props.canViewCost}>
              <NumberField
                control={props.control}
                name="costNet"
                label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
                onChange={follow('costNet', costNet)}
              />
            </Show>
          </PriceRow>
        </FormSection>
      </Show>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.offeredOn', 'Offered on')}>
        <Text size="xSmall" color="secondary">
          {t(
            'entity.newVehicle.catalogue.offeredOnHelp',
            'Customers can only pick this colour on the trims you select here.'
          )}
        </Text>
        <OfferedTrimsTable
          rows={trims.field.value}
          stored={props.stored}
          onChange={trims.field.onChange}
          trims={props.trims}
          itemName={name}
          currency={props.currency}
          vatRate={vatRate}
          canViewCost={props.canViewCost}
          newRowPrice={props.isCreate ? {priceNet, costNet: costNet ?? ''} : undefined}
          onSamePrice={props.onSamePrice}
          isDisabled={trims.formState.isSubmitting}
        />
      </FormSection>
      <HStack justify="flex-end" spacing={2}>
        <RetryReadButton read={props.colourRead} />
        <FormButton
          control={props.control}
          type="submit"
          title={props.submitTitle}
          isDisabled={props.colourRead?.isFetching || props.colourRead?.isError}
          isLoading={props.colourRead?.isFetching}
        />
      </HStack>
    </VStack>
  );
}

type UpholsteryFieldsProps = {
  control: FormControl<UpholsteryValues>;
  modelYearId: string;
  canViewCost: boolean;
  isCreate?: boolean;
  submitTitle: string;
};

function UpholsteryFields(props: UpholsteryFieldsProps) {
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const vatRate = useCatalogueVatRate(props.modelYearId);
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.section.identity', 'Identity')}>
        <FormField
          control={props.control}
          name="name"
          type="text"
          label={t('general.labels.name', 'Name')}
          placeholder="Ebony Cloth"
          isRequired={props.isCreate}
        />
        <Grid columns={3} spacing={3}>
          <FormField
            control={props.control}
            name="fabric"
            type="choice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.fabric', 'Fabric')}
            options={catalogue.interiorMaterialOptions ?? []}
            isLoading={isLoading}
            isDisabled={isNilOrEmpty(catalogue.interiorMaterialOptions)}
          />
          <FormField
            control={props.control}
            name="factoryCode"
            type="text"
            label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          />
          <FormField
            control={props.control}
            name="hex"
            type="text"
            label={t('entity.newVehicle.catalogue.hex', 'Hex')}
            placeholder="#1A2B3C"
            helperText={t(
              'entity.newVehicle.catalogue.hexHelp',
              'Used for the swatch salespeople see.'
            )}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.section.price', 'Price')}>
        <PriceRow vatRate={vatRate} canViewCost={props.canViewCost} spacing={3}>
          <GrossPriceField
            control={props.control}
            name="priceNet"
            vatRate={vatRate}
            label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
            helperText={t(
              'entity.newVehicle.catalogue.surchargeHelp',
              'Surcharge on top of the vehicle. Use 0 for a no-cost option.'
            )}
          />
          <Show when={props.canViewCost}>
            <NumberField
              control={props.control}
              name="costNet"
              label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
            />
          </Show>
        </PriceRow>
      </FormSection>
      <HStack justify="flex-end">
        <FormButton control={props.control} type="submit" title={props.submitTitle} />
      </HStack>
    </VStack>
  );
}

function SwatchName(props: {
  hex: string | Nullish;
  showSwatch: boolean;
  name: string;
  detail: string;
}) {
  return (
    <HStack spacing={3} align="center">
      <ColourSwatch hex={props.hex} reserveSlot={props.showSwatch} />
      <VStack spacing={0}>
        <Text size="small" alternative>
          {props.name}
        </Text>
        <Text size="xSmall" color="secondary">
          {props.detail}
        </Text>
      </VStack>
    </HStack>
  );
}

function ColourEditDialog(props: {
  colour: ColourResponseBody;
  trims: TrimResponseBody[];
  currency: string;
  canViewCost: boolean;
  reload: () => void;
}) {
  // A new pricelist version reprices the stored rows and bumps the colour's version; the editor
  // stays open on the colour as read again, so its saves compare with and write over that one.
  const [colour, setColour] = useState(props.colour);
  const [getColour, colourRead] = newVehicleApi.useLazyGetColourQuery();
  const [update] = useUpdateColourMutation();
  const [putTrims] = usePutColourTrimsMutation();
  const refreshVersion = useRefreshPersistedColourVersion();
  const persisted = useRef<PersistedColour | null>(null);
  const publishedPrice = usePublishedPriceSave(colour.modelYearId);
  const toValues = (read: ColourResponseBody): ColourValues => ({
    name: read.name,
    kind: read.kind ?? '',
    factoryCode: read.factoryCode ?? '',
    hex: read.hex ?? '',
    priceNet: '',
    costNet: '',
    trims: toOfferedTrimRows(props.trims, read.trims),
  });
  const defaultValues = toValues(colour);
  const save = async (values: ColourValues) => {
    const submittedValues = getColourScalarValues(values);
    if (
      !persisted.current ||
      JSON.stringify(persisted.current.values) !== JSON.stringify(submittedValues)
    ) {
      const previous = persisted.current;
      const updated = await update({
        colourId: previous?.id ?? colour.id,
        colourWriteRequestBody: {
          modelYearId: colour.modelYearId,
          name: values.name,
          codes: replaceFactoryCode(
            previous?.codes ?? colour.codes,
            previous?.values.factoryCode ?? colour.factoryCode,
            values.factoryCode
          ),
          kind: values.kind || null,
          hex: values.hex || null,
          factoryCode: values.factoryCode || null,
          version: previous?.version ?? colour.version,
        },
      }).unwrap();
      persisted.current = {
        id: updated.id,
        version: updated.version,
        codes: updated.codes,
        values: submittedValues,
      };
    }
    const {id, version} = persisted.current;
    const trims = toOfferedTrims(values.trims, colour.trims, props.canViewCost);
    const items = offeredTrimChanges({
      resourceType: 'colourTrim',
      trims: props.trims,
      stored: {name: colour.name, rows: colour.trims, isMember: true},
      next: {name: values.name, rows: trims, isMember: true},
    });
    // With an open group the scalar fields above are saved already (no price list change); the
    // trims join the group and are sent with the colour version read at Confirm (Q2).
    return publishedPrice.writePricedItemChange({
      items,
      entry: colourTrimsEntry({
        colour,
        name: values.name,
        trims,
        items,
        readColour: (colourId) => getColour({colourId}, false).unwrap(),
      }),
      write: async (pricelistVersion) => {
        const stored = await putTrims({
          colourId: id,
          replaceColourTrimsRequestBody: {
            trims,
            version,
            ...(pricelistVersion && {pricelistVersion}),
          },
        }).unwrap();
        // The rows are stored now: a retry after a later step failed compares with these, so a
        // version does not list a created row twice. The form keeps the prices the user typed.
        setColour((current) => ({...current, trims: stored}));
        await refreshVersion(persisted);
      },
    });
  };
  const submit: FormSubmitHandler<ColourValues> = (values, setErrors) =>
    (async () => {
      // Only a row in the price list before and after keeps its price until an adjustment sets it.
      const locked = memberRows(colour.trims, props.trims, true);
      const result = await publishedPrice.saveRows({
        values,
        heldBackValues: {...values, trims: withStoredPrices(values.trims, locked)},
        storedValues: defaultValues,
        changed: changedTrimPrices(values.trims, locked),
        isSavePending: persisted.current !== null,
        scope: 'colourTrim',
        save,
        onDone: closeCurrentDialog,
      });
      if (result === 'onePriceAtATime') {
        showNotification.error(publishedOnePriceAtATime());
      }
    })().catch(async (error: ApiError) => {
      handleCatalogueError(error, setErrors);
      if (!isStaleCatalogueVersion(error)) {
        return;
      }
      props.reload();
      await refreshVersion(persisted);
    });
  const readColourAgain = (form: UseFormReturn<ColourValues>) =>
    getColour({colourId: colour.id}, false)
      .unwrap()
      .then((current) => {
        rebaseForm(form, toValues(current));
        setColour(current);
        if (persisted.current) {
          persisted.current = {
            ...persisted.current,
            version: current.version,
            codes: current.codes,
          };
        }
      })
      .catch(handleCatalogueError);
  // On a published year the same price on all trims is a new pricelist version of the colour, once
  // it changes the price of a stored row in the price list; else the rows are filled in the form.
  const setSamePrice = (
    priceNet: string,
    onCreated: () => void,
    form: UseFormReturn<ColourValues>
  ) =>
    changesKeptPrice(colour.trims, props.trims, true, form.getValues('trims'), priceNet)
      ? publishedPrice.confirmAdjustment(
          {kind: 'set', value: priceNet, scope: 'colour', resourceIds: [colour.id]},
          {
            onDone: () => {
              onCreated();
              void readColourAgain(form);
            },
          }
        )
      : Promise.resolve(false);
  return (
    <>
      <Form<ColourValues>
        experimentalZodSchema={colourSchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control, form) => (
          <ColourFields
            control={control}
            modelYearId={colour.modelYearId}
            trims={props.trims}
            stored={colour.trims}
            currency={props.currency}
            canViewCost={props.canViewCost}
            submitTitle={t('general.actions.save', 'Save')}
            onSamePrice={(priceNet, onCreated) => setSamePrice(priceNet, onCreated, form)}
            colourRead={{
              isFetching: colourRead.isFetching,
              isError: colourRead.isError,
              retry: () => void readColourAgain(form),
            }}
          />
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}

function UpholsteryEditDialog({
  upholstery,
  canViewCost,
  reload,
}: {
  upholstery: UpholsteryResponseBody;
  canViewCost: boolean;
  reload: () => void;
}) {
  const [update] = useUpdateUpholsteryMutation();
  const publishedPrice = usePublishedPriceSave(upholstery.modelYearId);
  const defaultValues: UpholsteryValues = {
    name: upholstery.name,
    fabric: upholstery.fabric ?? '',
    factoryCode: upholstery.factoryCode ?? '',
    hex: upholstery.interiorColorHex ?? '',
    priceNet: upholstery.priceNet,
    costNet: upholstery.costNet ?? '',
  };
  const toUpdateRequest = (values: UpholsteryValues) => ({
    upholsteryId: upholstery.id,
    upholsteryWriteRequestBody: {
      modelYearId: upholstery.modelYearId,
      name: values.name,
      codes: replaceFactoryCode(upholstery.codes, upholstery.factoryCode, values.factoryCode),
      fabric: values.fabric || null,
      interiorColorHex: values.hex || null,
      factoryCode: values.factoryCode || null,
      priceNet: values.priceNet,
      costNet: canViewCost ? values.costNet || null : upholstery.costNet,
      version: upholstery.version,
    },
  });
  const submit: FormSubmitHandler<UpholsteryValues> = async (values, setErrors) => {
    await publishedPrice
      .save({
        values,
        storedValues: defaultValues,
        priceField: 'priceNet',
        scope: 'upholstery',
        resourceId: upholstery.id,
        save: (upholsteryValues) => update(toUpdateRequest(upholsteryValues)).unwrap(),
        onDone: closeCurrentDialog,
      })
      .catch((error: ApiError) => {
        handleCatalogueError(error, setErrors);
        if (isStaleCatalogueVersion(error)) {
          reload();
        }
      });
  };
  return (
    <>
      <Form<UpholsteryValues>
        experimentalZodSchema={upholsterySchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control) => (
          <UpholsteryFields
            control={control}
            modelYearId={upholstery.modelYearId}
            canViewCost={canViewCost}
            submitTitle={t('general.actions.save', 'Save')}
          />
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}

export function Colours({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListColourQuery({});
  const trimsQuery = useListTrimQuery({});
  const [create] = useCreateColourMutation();
  const [update] = useUpdateColourMutation();
  const [archive] = useArchiveColourMutation();
  const [putTrims] = usePutColourTrimsMutation();
  const refreshVersion = useRefreshPersistedColourVersion();
  const vatRate = useCatalogueVatRate(modelYearId);
  const publishedPrice = usePublishedPriceSave(modelYearId);
  const {isGroupOpen} = useOpenPricedItemGroup();
  const [getColour] = newVehicleApi.useLazyGetColourQuery();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const persisted = useRef<PersistedColour | null>(null);
  const rows =
    query.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ?? [];
  const showSwatch = hasAnySwatch(rows);
  const trims =
    trimsQuery.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ??
    [];
  const {getColorType} = useNewVehicleCatalogueLabels();
  // A new colour can be offered on a trim that the open group creates: it then joins the group.
  const groupedNewTrims = useGroupedNewTrims(modelYearId);
  const offerableTrims = [...trims, ...groupedNewTrims];
  const saveColour = async (values: ColourValues) => {
    const submittedValues = getColourScalarValues(values);
    // A grouped new trim picked here that left the group since is not sent: no write creates it.
    const offered = toOfferedTrims(values.trims, [], canViewCost).filter(({trimId}) =>
      offerableTrims.some(({id}) => id === trimId)
    );
    // The colour has no price of its own; its rows on online trims are new pricelist rows (D102).
    const items = offeredTrimChanges({
      resourceType: 'colourTrim',
      trims: offerableTrims,
      stored: {name: values.name, rows: [], isMember: true},
      next: {name: values.name, rows: offered, isMember: true},
    });
    const groupedTrimIds = groupedNewTrims.map(({id}) => id);
    const body = newColourBody(modelYearId, values);
    // With an open group, the create and its trims are held back and sent on Confirm (Q1).
    if (
      isNil(persisted.current) &&
      (items.length > 0 || offered.some(({trimId}) => groupedTrimIds.includes(trimId))) &&
      (await isGroupOpen(modelYearId))
    ) {
      return addToGroup(modelYearId, [
        {...colourCreateEntry({body, trims: offered, items, groupedTrimIds}), items},
      ]);
    }
    if (!persisted.current) {
      const colour = await create({colourWriteRequestBody: body}).unwrap();
      persisted.current = {
        id: colour.id,
        version: colour.version,
        codes: colour.codes,
        values: submittedValues,
      };
    } else if (JSON.stringify(persisted.current.values) !== JSON.stringify(submittedValues)) {
      const colour = await update({
        colourId: persisted.current.id,
        colourWriteRequestBody: {
          modelYearId,
          name: values.name,
          codes: replaceFactoryCode(
            persisted.current.codes,
            persisted.current.values.factoryCode,
            values.factoryCode
          ),
          kind: values.kind || null,
          hex: values.hex || null,
          factoryCode: values.factoryCode || null,
          version: persisted.current.version,
        },
      }).unwrap();
      persisted.current = {
        id: colour.id,
        version: colour.version,
        codes: colour.codes,
        values: submittedValues,
      };
    }
    const {id, version} = persisted.current;
    return publishedPrice.writePricedItemChange({
      items,
      write: (pricelistVersion) =>
        putTrims({
          colourId: id,
          replaceColourTrimsRequestBody: {
            trims: offered,
            version,
            ...(pricelistVersion && {pricelistVersion}),
          },
        }).unwrap(),
    });
  };
  // A retry after the trims failed or their version was cancelled updates the created colour
  // instead of creating another one.
  const submit: FormSubmitHandler<ColourValues> = (values, setErrors) =>
    saveColour(values)
      .then((outcome) => {
        if (outcome !== ITEM_CHANGE_CANCELLED) {
          closeCreate();
        }
      })
      .catch(async (error: ApiError) => {
        handleCatalogueError(error, setErrors);
        if (isStaleCatalogueVersion(error)) {
          await refreshVersion(persisted);
        }
      });
  const openCreate = () => {
    persisted.current = null;
    setCreateOpen(true);
  };
  const closeCreate = () => {
    persisted.current = null;
    setCreateOpen(false);
  };
  const editColour = (row: ColourResponseBody) =>
    openDialog(
      <ColourEditDialog
        colour={row}
        trims={trims}
        currency={currency}
        canViewCost={canViewCost}
        reload={() => void query.refetch()}
      />,
      {
        title: t('entity.newVehicle.catalogue.editColourNamed', 'Edit colour · {name}', {
          name: row.name,
        }),
      }
    );
  const archiveColour = (row: ColourResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveColour', 'Archive colour {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void publishedPrice
          .writePricedItemChange({
            ...colourArchiveChange(row, trims, (colourId) => getColour({colourId}, false).unwrap()),
            write: (pricelistVersion) =>
              archive({
                colourId: row.id,
                pricedItemArchiveRequestBody: {
                  version: row.version,
                  ...(pricelistVersion && {pricelistVersion}),
                },
              }).unwrap(),
          })
          .catch(handleCatalogueError),
    });
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.colours',
          'Exterior paints customers can choose. A colour only appears in the configurator on the trims it is offered on.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.tabs.colours', 'Colours')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addColour', 'Add colour'),
                  leftIcon: 'content/add',
                  onClick: openCreate,
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noColours', 'No colours yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.coloursHint',
            'A model year needs at least one colour before it can be published.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addColour', 'Add colour'),
                  leftIcon: 'content/add',
                  onClick: openCreate,
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.labels.colour', 'Colour')),
              column(t('entity.newVehicle.catalogue.priceByTrim', 'Price by trim'), {
                width: '45%',
              }),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => editColour(row) : undefined}>
                <Cell>
                  <SwatchName
                    hex={row.hex}
                    showSwatch={showSwatch}
                    name={row.name}
                    detail={joinParts([getColorType(row.kind), row.factoryCode])}
                  />
                </Cell>
                <Cell>
                  <TrimPrices
                    rows={row.trims}
                    trims={trims}
                    currency={currency}
                    vatRate={vatRate}
                    canViewCost={canViewCost}
                  />
                </Cell>
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => editColour(row)),
                        archiveRowAction(() => archiveColour(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={isCreateOpen}
          title={t('entity.newVehicle.catalogue.addColour', 'Add colour')}
          onClose={closeCreate}
        >
          <Form<ColourValues>
            experimentalZodSchema={colourSchema}
            defaultValues={{
              name: '',
              kind: '',
              factoryCode: '',
              hex: '',
              priceNet: '',
              costNet: '',
              trims: [],
            }}
            onSubmit={submit}
          >
            {(control) => (
              <ColourFields
                control={control}
                modelYearId={modelYearId}
                trims={offerableTrims}
                stored={[]}
                currency={currency}
                canViewCost={canViewCost}
                isCreate
                submitTitle={t('entity.newVehicle.catalogue.addColour', 'Add colour')}
              />
            )}
          </Form>
        </Dialog>
      </Card>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </VStack>
  );
}

const hasAnyUpholsterySwatch = (rows: UpholsteryResponseBody[]) =>
  hasAnySwatch(rows.map(({interiorColorHex}) => ({hex: interiorColorHex})));

// Both matrices put upholstery across the top; tiles keep a fixed width so the grid stays even.
const upholsteryColumns = (
  rows: UpholsteryResponseBody[],
  currency: string,
  firstLabel: string
) => {
  const showUpholsterySwatch = hasAnyUpholsterySwatch(rows);
  return [
    column(firstLabel, {width: 60}),
    ...rows.map((item) =>
      column(
        <SwatchLabel
          name={item.name}
          hex={item.interiorColorHex}
          showSwatch={showUpholsterySwatch}
          amount={item.priceNet}
          currency={currency}
          direction="column"
        />,
        {width: TOGGLE_COLUMN_WIDTH, align: 'center'}
      )
    ),
    // Filler so the fixed-width tiles hug the left instead of spreading across the card.
    column(''),
  ];
};

export function Upholsteries({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListUpholsteryQuery({});
  const trimsQuery = useListTrimQuery({});
  const [create] = useCreateUpholsteryMutation();
  const [archive] = useArchiveUpholsteryMutation();
  const [getUpholstery] = newVehicleApi.useLazyGetUpholsteryQuery();
  const [putTrim] = usePutTrimUpholsteryMutation();
  const publishedPrice = usePublishedPriceSave(modelYearId);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const {enqueueSave, readTrimUpholstery} = useMatrixSaveQueue(modelYearId);
  const vatRate = useCatalogueVatRate(modelYearId);
  const ofModelYear = (items: UpholsteryResponseBody[] = []) =>
    items.filter((item) => item.modelYearId === modelYearId && !item.archived);
  const rows = ofModelYear(query.data?.items);
  const showSwatch = hasAnyUpholsterySwatch(rows);
  const currentIdsFor = (trimId: string, items = rows) =>
    items.filter(({availableForTrimIds}) => availableForTrimIds.includes(trimId)).map(({id}) => id);
  const trims =
    trimsQuery.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ??
    [];
  const {getInteriorMaterial} = useNewVehicleCatalogueLabels();
  const setTrimUpholstery = (trimId: string, upholsteryId: string, available: boolean) =>
    enqueueSave(async () => {
      const [trimList, upholsteryList] = await readTrimUpholstery();
      const trim = trimList.items.find(({id}) => id === trimId);
      if (isNil(trim)) {
        return;
      }
      const others = currentIdsFor(trimId, ofModelYear(upholsteryList.items)).filter(
        (id) => id !== upholsteryId
      );
      await putTrim({
        trimId,
        replaceTrimUpholsteryRequestBody: {
          upholsteryIds: available ? [...others, upholsteryId] : others,
          version: trim.version,
        },
      }).unwrap();
      await readTrimUpholstery();
    });
  const submit: FormSubmitHandler<UpholsteryValues> = (values, setErrors) => {
    const body: UpholsteryWriteRequestBody = {
      modelYearId,
      name: values.name,
      codes: values.factoryCode ? [values.factoryCode] : [],
      fabric: values.fabric || null,
      interiorColorHex: values.hex || null,
      factoryCode: values.factoryCode || null,
      priceNet: values.priceNet,
      costNet: canViewCost ? values.costNet || null : undefined,
      version: 1,
    };
    return publishedPrice
      .writePricedItemChange({
        ...upholsteryCreateChange(body),
        write: (pricelistVersion) =>
          create({
            upholsteryWriteRequestBody: {...body, ...(pricelistVersion && {pricelistVersion})},
          }).unwrap(),
      })
      .then((outcome) => {
        // A cancelled pricelist version keeps the form open with its values.
        if (outcome !== ITEM_CHANGE_CANCELLED) {
          setCreateOpen(false);
        }
      })
      .catch((error: ApiError) => handleCatalogueError(error, setErrors));
  };
  const editUpholstery = (row: UpholsteryResponseBody) =>
    openDialog(
      <UpholsteryEditDialog
        upholstery={row}
        canViewCost={canViewCost}
        reload={() => void query.refetch()}
      />,
      {
        title: t('entity.newVehicle.catalogue.editUpholsteryNamed', 'Edit upholstery · {name}', {
          name: row.name,
        }),
      }
    );
  const archiveUpholstery = (row: UpholsteryResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveUpholstery', 'Archive upholstery {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void publishedPrice
          .writePricedItemChange({
            ...upholsteryArchiveChange(row, (upholsteryId) =>
              getUpholstery({upholsteryId}, false).unwrap()
            ),
            write: (pricelistVersion) =>
              archive({
                upholsteryId: row.id,
                pricedItemArchiveRequestBody: {
                  version: row.version,
                  ...(pricelistVersion && {pricelistVersion}),
                },
              }).unwrap(),
          })
          .catch(handleCatalogueError),
    });
  return (
    <VStack spacing={4}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.upholsteryTrims',
          'Interior trims customers can choose. Set which trims offer each one, then block any colour combinations the factory does not build on the Colour & upholstery tab.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.labels.upholstery', 'Upholstery')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noUpholstery', 'No upholstery yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.upholsteryHint',
            'A model year needs at least one upholstery before it can be published.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.labels.upholstery', 'Upholstery')),
              column(t('entity.newVehicle.catalogue.offeredOn', 'Offered on'), {width: '30%'}),
              column(t('entity.newVehicle.catalogue.priceNet', 'Price net'), {
                width: '14%',
                align: 'right',
              }),
              ...(canViewCost
                ? [
                    column(t('entity.newVehicle.catalogue.costNet', 'Cost net'), {
                      width: '14%',
                      align: 'right',
                    }),
                  ]
                : []),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => editUpholstery(row) : undefined}>
                <Cell>
                  <SwatchName
                    hex={row.interiorColorHex}
                    showSwatch={showSwatch}
                    name={row.name}
                    detail={joinParts([getInteriorMaterial(row.fabric), row.factoryCode])}
                  />
                </Cell>
                <Cell>
                  <OfferedOn trimIds={row.availableForTrimIds} trims={trims} />
                </Cell>
                <Cell align="right">
                  <PriceWithGross amount={row.priceNet} currency={currency} vatRate={vatRate} />
                </Cell>
                {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
                {canViewCost ? (
                  <Cell align="right">
                    <MoneyText amount={row.costNet ?? '0.00'} currency={currency} />
                  </Cell>
                ) : null}
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => editUpholstery(row)),
                        archiveRowAction(() => archiveUpholstery(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={isCreateOpen}
          title={t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery')}
          onClose={() => setCreateOpen(false)}
        >
          <Form<UpholsteryValues>
            experimentalZodSchema={upholsterySchema}
            defaultValues={EMPTY_UPHOLSTERY}
            onSubmit={submit}
          >
            {(control) => (
              <UpholsteryFields
                control={control}
                modelYearId={modelYearId}
                canViewCost={canViewCost}
                isCreate
                submitTitle={t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery')}
              />
            )}
          </Form>
        </Dialog>
        <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
      </Card>
      <Show when={rows.length > 0 && trims.length > 0}>
        <Card
          title={t(
            'entity.newVehicle.catalogue.trimUpholsteryQuestion',
            'Which upholstery comes with each trim?'
          )}
          subtitle={t(
            'entity.newVehicle.catalogue.trimUpholsteryHint',
            'Click a cell to choose which upholstery a customer can pick on each trim.'
          )}
        >
          <Table
            columns={upholsteryColumns(
              rows,
              currency,
              t('entity.newVehicle.catalogue.trimLevel', 'Trim level')
            )}
          >
            {trims.map((trim) => (
              <TableRow key={trim.id}>
                <Cell>
                  <VStack spacing={0}>
                    <Text size="small" alternative>
                      {trim.name}
                    </Text>
                    <Show when={!currentIdsFor(trim.id).length}>
                      <Text size="xSmall" color="warning">
                        {t(
                          'entity.newVehicle.catalogue.trimWithoutUpholstery',
                          'No upholstery selected'
                        )}
                      </Text>
                    </Show>
                  </VStack>
                </Cell>
                {rows.map((item) => {
                  const checked = currentIdsFor(trim.id).includes(item.id);
                  return (
                    <Cell key={item.id} align="center">
                      <ToggleTile
                        isDisabled={!canWrite}
                        isOn={checked}
                        aria-label={`${trim.name}, ${item.name}`}
                        onToggle={() => setTrimUpholstery(trim.id, item.id, !checked)}
                      />
                    </Cell>
                  );
                })}
                <Cell>{null}</Cell>
              </TableRow>
            ))}
          </Table>
        </Card>
      </Show>
    </VStack>
  );
}

export function ColourUpholsteryCombinations(props: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
}) {
  const query = useListUpholsteryQuery({});
  const coloursQuery = useListColourQuery({});
  const restrictionsQuery = useGetColourUpholsteryRestrictionsQuery({
    modelYearId: props.modelYearId,
  });
  const [putRestrictions] = usePutColourUpholsteryRestrictionsMutation();
  const {enqueueSave, readRestrictions} = useMatrixSaveQueue(props.modelYearId);
  const rows =
    query.data?.items.filter((item) => item.modelYearId === props.modelYearId && !item.archived) ??
    [];
  const colours =
    coloursQuery.data?.items.filter(
      (item) => item.modelYearId === props.modelYearId && !item.archived
    ) ?? [];
  const restrictions = restrictionsQuery.data?.restrictions ?? [];
  const isRestricted = (colourId: string, upholsteryId: string) =>
    restrictions.some((pair) => pair.colourId === colourId && pair.upholsteryId === upholsteryId);
  const setRestricted = (colourId: string, upholsteryId: string, restricted: boolean) =>
    enqueueSave(async () => {
      const latest = await readRestrictions();
      const others = latest.restrictions.filter(
        (pair) => pair.colourId !== colourId || pair.upholsteryId !== upholsteryId
      );
      await putRestrictions({
        modelYearId: props.modelYearId,
        replaceColourUpholsteryRestrictionsRequestBody: {
          version: latest.version,
          restrictions: restricted ? [...others, {colourId, upholsteryId}] : others,
        },
      }).unwrap();
      await readRestrictions();
    });
  const showColourSwatch = hasAnySwatch(colours);
  return (
    <Card
      title={t(
        'entity.newVehicle.catalogue.colourUpholsteryQuestion',
        'Which colour and upholstery combinations can be ordered?'
      )}
      subtitle={t(
        'entity.newVehicle.catalogue.colourUpholsteryHint',
        'All combinations are allowed by default. Click a cell to block one the factory does not build.'
      )}
    >
      <DataStatus
        isLoading={query.isLoading || coloursQuery.isLoading || restrictionsQuery.isLoading}
        isError={query.isError || coloursQuery.isError || restrictionsQuery.isError}
        isEmpty={!rows.length || !colours.length}
        emptyMessage={t(
          'entity.newVehicle.catalogue.colourUpholsteryEmpty',
          'Add colours and upholstery first'
        )}
      >
        <Table
          columns={upholsteryColumns(
            rows,
            props.currency,
            t('entity.newVehicle.labels.colour', 'Colour')
          )}
        >
          {colours.map((colour) => (
            <TableRow key={colour.id}>
              <Cell>
                <SwatchLabel
                  name={colour.name}
                  hex={colour.hex}
                  showSwatch={showColourSwatch}
                  currency={props.currency}
                  direction="row"
                />
              </Cell>
              {rows.map((item) => (
                <Cell key={item.id} align="center">
                  <ToggleTile
                    isDisabled={!props.canWrite || !restrictionsQuery.data}
                    isOn={!isRestricted(colour.id, item.id)}
                    aria-label={`${colour.name}, ${item.name}`}
                    onToggle={() =>
                      setRestricted(colour.id, item.id, !isRestricted(colour.id, item.id))
                    }
                  />
                </Cell>
              ))}
              <Cell>{null}</Cell>
            </TableRow>
          ))}
        </Table>
      </DataStatus>
    </Card>
  );
}
